import { motion } from 'framer-motion'
import { User, Copy, Globe, ExternalLink } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { nip19 } from 'nostr-tools'
import { useNostrProfile } from '../hooks/useNostrProfile'
import { useNostrUserEvents } from '../hooks/useNostrUserEvents'
import { EntryCard } from '../components/Entry/EntryCard'
import { useMemo, useState } from 'react'
import { useChatStore } from '../stores/chatStore'
import { ZapModal } from '../components/Social/ZapModal'
import { useFollowList } from '../hooks/useFollowList'
import { publishFollowList } from '../lib/nostr'
import { useToast } from '../contexts/ToastContext'
import { ProfileQRModal } from '../components/Profile/ProfileQRModal'
import { ProfileActions } from '../components/Profile/ProfileActions'
import { VerificationBadge } from '../components/Profile/VerificationBadge'
import { UserAvatar } from '../components/Common/UserAvatar'

export function ExternalProfilePage() {
    const { username } = useParams<{ username: string }>()
    const { pubkey: currentUserPubkey, sk } = useAuth()
    const [isZapModalOpen, setIsZapModalOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<'entries' | 'about'>('entries')
    const { openChat } = useChatStore()
    const { showToast } = useToast()
    const [qrModalOpen, setQrModalOpen] = useState(false)
    const [isFollowLoading, setIsFollowLoading] = useState(false)

    // Determine the hex pubkey first
    const pubkey = useMemo(() => {
        if (!username) return null

        if (username.startsWith('npub1')) {
            try {
                const decoded = nip19.decode(username) as any
                if (decoded.type === 'npub') {
                    return decoded.data
                }
            } catch (e) {
                console.error('Failed to decode npub:', e)
                return null
            }
        }
        return username
    }, [username])

    // Get current user's following list
    const { following, isFollowing, refresh: refreshFollowList, updateFollowing } = useFollowList(currentUserPubkey)

    // Handle follow/unfollow
    const handleFollowToggle = async () => {
        if (!currentUserPubkey || !pubkey || isFollowLoading) return

        setIsFollowLoading(true)

        try {
            const currentlyFollowing = isFollowing(pubkey)
            let newFollowingList: string[]

            if (currentlyFollowing) {
                newFollowingList = following.filter(p => p !== pubkey)
            } else {
                newFollowingList = [...following, pubkey]
            }

            // Immediately update UI (optimistic)
            updateFollowing(newFollowingList)

            // Publish to Nostr
            await publishFollowList(newFollowingList, sk)

            // Refresh from relays
            setTimeout(() => {
                refreshFollowList()
            }, 500)

            showToast(currentlyFollowing ? 'Takipten çıkıldı' : 'Takip edildi', 'success')
        } catch (error) {
            console.error('Follow toggle error:', error)
            showToast('Bir hata oluştu', 'error')
        } finally {
            setIsFollowLoading(false)
        }
    }

    // Fetch data - allowGlobal: true to bypass mavililer filter
    const { metadata } = useNostrProfile(pubkey)
    const { events, isLoading: eventsLoading, isLinking, loadMore } = useNostrUserEvents(pubkey, true)

    const isOwnProfile = pubkey === currentUserPubkey

    const profileName = metadata?.display_name || metadata?.name || (pubkey ? `${pubkey.slice(0, 8)}...` : 'Anonim')
    const npub = pubkey ? nip19.npubEncode(pubkey) : ''

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        showToast(`${label} kopyalandı!`, 'success')
    }


    if (!pubkey) {
        return (
            <div className="text-center py-12">
                <User className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <h2 className="font-poppins font-semibold text-2xl mb-4">Profil Bulunamadı</h2>
                <p className="text-gray-600 dark:text-gray-400">Geçersiz kullanıcı kimliği.</p>
            </div>
        )
    }

    const mappedEntries = events.map(event => {
        const subjectTag = event.tags.find(t => t[0] === 'subject')?.[1]
        const topicTag = event.tags.find(t => t[0] === 't' && t[1] !== 'mavililer' && t[1] !== 'mavililersözlük')?.[1]
        const topicTitle = (subjectTag || topicTag || 'Global Akış').replace(/-/g, ' ')

        return {
            id: event.id,
            content: event.content,
            created_at: new Date(event.created_at * 1000).toISOString(),
            author_pubkey: event.pubkey,
            topicTitle: topicTitle,
            author_username: profileName, // Use profileName from metadata
            author_avatar_url: metadata?.picture || null, // Use metadata picture
            author_reputation: 0,
            upvotes: 0,
            downvotes: 0,
        }
    })

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
            >
                {/* Profile Header - Hybrid Layout */}
                <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-sm border border-surface-200 dark:border-surface-700 overflow-hidden">
                    {/* Banner */}
                    <div className="relative h-48 md:h-56 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-surface-800 dark:to-surface-950" />

                    {/* Profile Content */}
                    <div className="relative px-4 md:px-6 pb-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="relative -mt-16 md:-mt-20">
                                <UserAvatar
                                    src={metadata?.picture}
                                    name={profileName}
                                    size="2xl"
                                    isExternal={true}
                                    className="shadow-xl"
                                />
                            </div>

                            {/* Action Buttons */}
                            <ProfileActions
                                isOwnProfile={isOwnProfile}
                                isFollowing={pubkey ? isFollowing(pubkey) : false}
                                isFollowLoading={isFollowLoading}
                                onEdit={() => { }} // Not editable on external profile view
                                onQR={() => setQrModalOpen(true)}
                                onFollow={handleFollowToggle}
                                onZap={() => setIsZapModalOpen(true)}
                                onMessage={() => openChat(pubkey!)}
                            />
                        </div>

                        {/* Profile Info */}
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center gap-3 flex-wrap mb-1">
                                    <h1 className="font-poppins font-bold text-2xl md:text-3xl text-gray-900 dark:text-white">
                                        {profileName}
                                    </h1>
                                    <VerificationBadge nip05={metadata?.nip05} />
                                    <span className="px-2 py-0.5 bg-surface-100 dark:bg-surface-800 text-gray-500 dark:text-gray-400 text-[10px] font-poppins font-bold uppercase tracking-wider rounded-md border border-surface-200 dark:border-surface-700">
                                        Dış Kaynak
                                    </span>
                                </div>
                                {metadata?.nip05 && (
                                    <p className="text-sm text-mavi-600 dark:text-mavi-400 font-poppins">
                                        {metadata.nip05}
                                    </p>
                                )}
                            </div>

                            {metadata?.about && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-merriweather max-w-2xl leading-relaxed">
                                    {metadata.about}
                                </p>
                            )}

                            <div className="flex flex-wrap gap-4 text-sm font-poppins">
                                {metadata?.website && (
                                    <a href={metadata.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-gray-500 hover:text-mavi-600 transition-colors">
                                        <Globe className="w-4 h-4" />
                                        Web Sitesi
                                    </a>
                                )}
                                <button onClick={() => setQrModalOpen(true)} className="flex items-center gap-1.5 text-gray-500 hover:text-mavi-600 transition-colors">
                                    <Copy className="w-4 h-4" />
                                    QR Kod
                                </button>
                                <a href={`https://njump.me/${npub}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-gray-500 hover:text-mavi-600 transition-colors">
                                    <ExternalLink className="w-4 h-4" />
                                    Njump'da Gör
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-sm border border-surface-200 dark:border-surface-700">
                    <div className="flex border-b border-surface-200 dark:border-surface-700">
                        <button
                            onClick={() => setActiveTab('entries')}
                            className={`px-6 py-4 font-poppins font-bold text-sm transition-all relative ${activeTab === 'entries' ? 'text-mavi-600' : 'text-gray-500'}`}
                        >
                            Gönderiler
                            {activeTab === 'entries' && <motion.div layoutId="extTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-mavi-600" />}
                        </button>
                        <button
                            onClick={() => setActiveTab('about')}
                            className={`px-6 py-4 font-poppins font-bold text-sm transition-all relative ${activeTab === 'about' ? 'text-mavi-600' : 'text-gray-500'}`}
                        >
                            Hakkında
                            {activeTab === 'about' && <motion.div layoutId="extTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-mavi-600" />}
                        </button>
                    </div>

                    <div className="p-6">
                        {activeTab === 'entries' && (
                            <div className="space-y-4">
                                {eventsLoading ? (
                                    <div className="space-y-4">
                                        {[1, 2, 3].map(i => <div key={i} className="h-32 bg-surface-50 dark:bg-surface-800 animate-pulse rounded-xl" />)}
                                    </div>
                                ) : mappedEntries.length > 0 ? (
                                    <>
                                        {mappedEntries.map(entry => <EntryCard key={entry.id} entry={entry} />)}

                                        <div className="pt-8 flex justify-center">
                                            <button
                                                onClick={loadMore}
                                                disabled={isLinking}
                                                className="group relative flex items-center gap-2 px-8 py-3 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-xl font-poppins font-bold text-sm text-gray-600 dark:text-gray-400 hover:text-mavi-600 dark:hover:text-mavi-400 hover:border-mavi-500/50 transition-all shadow-sm hover:shadow-md disabled:opacity-50"
                                            >
                                                {isLinking ? (
                                                    <div className="w-5 h-5 border-2 border-mavi-500 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span>Daha Fazlasını Yükle</span>
                                                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-surface-100 dark:bg-surface-800 group-hover:bg-mavi-50 dark:group-hover:bg-mavi-950 transition-colors">
                                                            <svg className="w-3 h-3 transition-transform group-hover:translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                )}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-12 text-gray-500 font-poppins">Henüz gönderi bulunamadı.</div>
                                )}
                            </div>
                        )}
                        {activeTab === 'about' && (
                            <div className="space-y-8">
                                <div className="space-y-3">
                                    <p className="font-merriweather text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                                        {metadata?.about || 'Bu kullanıcı hakkında henüz bilgi bulunmuyor.'}
                                    </p>
                                </div>

                                {/* Nostr Identity */}
                                <div className="space-y-6 pt-6 border-t border-surface-200 dark:border-surface-700">
                                    <div className="space-y-4">
                                        <h4 className="text-[10px] font-poppins font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                                            Kimlik Bilgileri
                                        </h4>

                                        <div
                                            onClick={() => handleCopy(npub, 'Kısa Kimlik')}
                                            className="group p-4 bg-surface-50 dark:bg-surface-800/30 rounded-2xl border border-surface-100 dark:border-surface-800 hover:border-mavi-500/30 transition-all cursor-pointer"
                                        >
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-[9px] font-poppins font-bold text-mavi-600 dark:text-mavi-400 uppercase tracking-widest">Kısa Kimlik (npub)</span>
                                                <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-mavi-500 transition-colors" />
                                            </div>
                                            <p className="text-[11px] md:text-xs font-mono text-gray-600 dark:text-gray-400 break-all leading-relaxed">
                                                {npub}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-4">
                                        <a href={`https://njump.me/${npub}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs font-poppins font-medium text-gray-500 hover:text-mavi-600 transition-colors">
                                            <ExternalLink className="w-4 h-4" />
                                            Njump'da Görüntüle
                                        </a>
                                        {metadata?.website && (
                                            <a href={metadata.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs font-poppins font-medium text-gray-500 hover:text-mavi-600 transition-colors">
                                                <Globe className="w-4 h-4" />
                                                Web Sitesini Ziyaret Et
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div >

            <ZapModal
                isOpen={isZapModalOpen}
                onClose={() => setIsZapModalOpen(false)}
                recipientName={profileName}
                lnAddress={metadata?.lud16}
            />
            <ProfileQRModal
                isOpen={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                pubkey={pubkey}
                profileName={profileName}
            />
        </>
    )
}
